import React from 'react';

import {TRANSFORMATION_STEP_TYPES} from 'analysis-tool/AnalysisToolConstants';
import {AnalysisGraphQL, AnalysisViewGraphQL} from 'analysis-tool/AnalysisTypes';
import {isViewTerminalStepType} from 'analysis-tool/data/LineageStepTypes';
import {getOutputDataForView} from 'analysis-tool/data-view/AnalysisViewUtils';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import AddTransformStepDropdown from 'analysis-tool/ui/charting/AddTransformStepDropdown';
import {BLOCKED_BY_DOWNSTREAM_FLAG_ROWS_MESSAGE} from 'analysis-tool/ui/charting/AddTransformStepDropdown';
import {FIX_FAILED_TRANSFORMS_MESSAGE} from 'analysis-tool/ui/charting/AddTransformStepDropdown';
import TransformPill from 'analysis-tool/ui/charting/TransformPill';
import {getTransformPills} from 'analysis-tool/ui/charting/TransformPillUtils';
import AdditionalEditorSection from 'analysis-tool/ui/components/AdditionalEditorSection';
import isArchived from 'archive/isArchived';
import {PipelineStepDataKind, PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import hasDownstreamFlagRowsStep from 'pipelines/hasDownstreamFlagRowsStep';
import {PipelineStepRawData} from 'pipelines/PipelineTypes';
import {getLegacyStepFromAnalysis} from 'pipelines/StepUtil';

interface EditViewTransformsProps {
  analysis: AnalysisGraphQL;
  view: AnalysisViewGraphQL;
  dataFrameSpec: PipelineStepRawData<PipelineStepDataKind.DATASET> | null;
}

export default function EditViewTransforms({
  analysis,
  view,
  dataFrameSpec,
}: EditViewTransformsProps): JSX.Element {
  const {analysisToolEnableConsolidatedFilter} = useRolloutFlags();
  const viewSteps = view.lineage.map(({step}) => getLegacyStepFromAnalysis(analysis, step));
  const allStepsSucceeded = viewSteps.every(
    (step, index) => step.statusCode === PipelineStepStatusCode.SUCCEEDED || index === viewSteps.length - 1
  );
  const blockedByDownstream = hasDownstreamFlagRowsStep(analysis, viewSteps[viewSteps.length - 1]);
  const canAddNewSteps = allStepsSucceeded && !blockedByDownstream;
  const addNewStepsDisabledMessage =
    (!allStepsSucceeded ? FIX_FAILED_TRANSFORMS_MESSAGE : undefined) ??
    (blockedByDownstream ? BLOCKED_BY_DOWNSTREAM_FLAG_ROWS_MESSAGE : undefined);

  // getOutputDataForView works for top level, but for view level the last step in the view lineage is a terminal step,
  // so we need the input data frame instead.
  const endsInTerminalViewStep = isViewTerminalStepType(viewSteps[viewSteps.length - 1].definition.stepType);
  // might be undefined if step is running/blocked, failed, etc.
  const maybeInputDataFrameId =
    endsInTerminalViewStep && dataFrameSpec != null ? dataFrameSpec.id : getOutputDataForView(view)?.id;

  const transformPills = analysisToolEnableConsolidatedFilter
    ? view.lineage
        .filter(({step}) => TRANSFORMATION_STEP_TYPES.includes(step.stepType))
        .map(({step}) => (
          <TransformPill key={step.id} analysis={analysis} step={getLegacyStepFromAnalysis(analysis, step)} />
        ))
    : getTransformPills(analysis, viewSteps);

  return (
    <AdditionalEditorSection
      title="Filters & Transforms"
      addItemElement={
        maybeInputDataFrameId && !isAnalysisReadOnly(analysis) && !isArchived(analysis) ? (
          <AddTransformStepDropdown
            viewSteps={viewSteps}
            dataFrameSpec={dataFrameSpec}
            inputDataFrameId={maybeInputDataFrameId}
            analysis={analysis}
            canAddNewSteps={canAddNewSteps}
            addNewStepsDisabledMessage={addNewStepsDisabledMessage}
          />
        ) : (
          <></>
        )
      }
      pills={transformPills}
      showTopBorder={false}
    />
  );
}
